import { useEffect, useState } from "react";
import { Sparkles, Bot, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin-only panel on /settings that lets the workspace owner flip the
 * default OCR provider. The setting lives in `platform_settings` and is
 * read by `ocr_service.extract_statement` whenever a caller doesn't pass
 * an explicit `model` override.
 */
export default function OcrProviderSetting() {
    const [provider, setProvider] = useState("gemini");
    const [busy, setBusy] = useState(false);
    const [initial, setInitial] = useState(null);

    useEffect(() => {
        api.get("/admin/platform-settings")
            .then((r) => {
                setProvider(r.data?.ocr_provider || "gemini");
                setInitial(r.data?.ocr_provider || "gemini");
            })
            .catch(() => { /* silent */ });
    }, []);

    const save = async (next) => {
        setBusy(true);
        try {
            const r = await api.patch("/admin/platform-settings", { ocr_provider: next });
            setProvider(r.data?.ocr_provider || next);
            setInitial(r.data?.ocr_provider || next);
            toast.success(`OCR default set to ${next === "gpt" ? "GPT 5.6 Sol" : "Gemini 2.5"}`);
        } catch {
            toast.error("Couldn't save. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const OPTIONS = [
        {
            id: "gemini",
            name: "Gemini 2.5 Flash",
            icon: Bot,
            pill: "Recommended",
            desc: "Fastest and cheapest — great for clean statement PDFs from major issuers.",
        },
        {
            id: "gpt",
            name: "GPT 5.6 Sol",
            icon: Sparkles,
            pill: "Premium",
            desc: "Best on messy scans, image-only PDFs and unusual layouts. Slower and pricier per page.",
        },
    ];

    return (
        <div className="mb-8 rounded-xl border border-violet-200 bg-violet-50/30 p-5" data-testid="ocr-provider-setting">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-violet-900 font-semibold">
                        Statement OCR · Default provider
                    </div>
                    <div className="text-sm text-stone-700 mt-1 max-w-xl">
                        Which AI parses uploaded statement PDFs by default. Individual uploads
                        can still override this. If one provider errors, VAULT auto-falls-back
                        to the other so uploads never fail because of a bad-provider day.
                    </div>
                </div>
                {initial && initial !== provider && (
                    <span className="text-[11px] text-amber-700 font-medium">Unsaved change</span>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OPTIONS.map((opt) => {
                    const active = provider === opt.id;
                    return (
                        <button
                            key={opt.id}
                            onClick={() => save(opt.id)}
                            disabled={busy}
                            className={`text-left rounded-xl p-4 border transition-all ${
                                active
                                    ? "border-violet-500 bg-white ring-2 ring-violet-200"
                                    : "border-stone-200 bg-white hover:border-stone-400"
                            } disabled:opacity-60`}
                            data-testid={`ocr-provider-${opt.id}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                                    active ? "bg-violet-600 text-white" : "bg-stone-100 text-stone-700"
                                }`}>
                                    <opt.icon size={16} />
                                </span>
                                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                    opt.id === "gemini"
                                        ? "bg-emerald-100 text-emerald-800"
                                        : "bg-amber-100 text-amber-800"
                                }`}>
                                    {opt.pill}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="text-[14.5px] font-semibold text-stone-900">{opt.name}</div>
                                {active && <Check size={14} className="text-violet-600" />}
                            </div>
                            <div className="text-[12px] text-stone-500 mt-1 leading-snug">{opt.desc}</div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
